import React, { useCallback, useEffect, useRef, useState } from 'react';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { createCar } from '../../../api/carsApi';
import { EventoLookupResult, EventoLocal, EventoLocalCar, EventoVeiculo, HinovaStatus } from '../../../api/eventosApi';
import { fetchFipeBrands, fetchFipeModels, FipeBrand, FipeModel } from '../../../api/fipeApi';
import { useEventoLookup } from '../../../hooks/useEventoLookup';
import { usePlateCheck } from '../../../hooks/usePlateCheck';
import ConfirmModal from '../confirm/ConfirmModal';
import FipePickerModal, { PickerItem } from '../brand-picker/BrandPickerModal';
import { CarClass } from '../../../interfaces/Car';
import {
  CarClassOptions,
  CarClassMap,
  getCarClassKeyFromLabel,
  DamageType,
  DamageTypeMap,
  DamageTypeOptions,
} from '../../../components/mappings/textMappings';
import { OBSERVACOES_MAX_LENGTH } from '../../../utils/textLimits';
import { formatPhoneBr } from '../../../utils/phoneMask';
import { notify } from '../../../shared/feedback/notify';
import { CHAVE_CADASTRO_VEICULO } from '../../../api/rascunhosApi';
import { useRascunho } from '../../../hooks/useRascunho';
import RascunhoRestaurado from '../../rascunho/RascunhoRestaurado';
import ComoFunciona, { type ComoFuncionaSecao } from '../../como-funciona/ComoFunciona';
import '../../como-funciona/help-circle-btn.css';
import './AddCarModal.css';
// A faixa de erro é a classe compartilhada. Importar aqui, e não contar com o CSS da página
// hospedeira: é o acoplamento invisível do BUG-V74 (o bundle único do CRA esconde a falta).
import '../../../shared/feedback/erro-de-tela.css';

/** Placa canônica: maiúsculas, só alfanuméricos. A mesma regra do `normalizePlate` do backend. */
const placaCanonica = (placa: string): string => (placa ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/**
 * at-106 — um veículo do evento ORIGINAL, candidato a ser a origem do pós-evento.
 *
 * 🔴 É aqui que vive a resposta de "este veículo já passou pelo SOAC?", que o Victor decidiu em
 * 16/09/2026 que o sistema responde sozinho. A recepção escolhe o VEÍCULO; o candidato carrega o
 * que o sistema sabe dele, e é isso que decide o que será gravado.
 */
interface CandidatoDeOrigem {
  /** Placa canônica — a chave que une os dois lados da resposta do lookup. */
  plate: string;
  model: string | null;
  carClassLabel: string | null;
  /**
   * O atendimento nosso daquele evento, quando existe e já foi ENTREGUE.
   * Preenchido ⇒ origem no sistema ⇒ grava `posEventoDeCarId`.
   */
  atendimento: EventoLocalCar | null;
  /** O veículo como o SGA o devolveu, quando a Hinova respondeu. Alimenta o pré-preenchimento. */
  veiculoSga: EventoVeiculo | null;
}

/**
 * Une os dois lados da resposta do lookup do evento ORIGINAL numa lista só de candidatos.
 *
 * 🔴 A união é pela PLACA CANÔNICA, e a pergunta "já passou pelo SOAC?" é respondida por veículo,
 * nunca pelo evento inteiro. As duas coisas divergem num caso real: evento com associado atendido
 * aqui e terceiro atendido fora, com o terceiro voltando. Perguntar "o evento tem algum veículo
 * entregue?" acertaria o associado e erraria o terceiro, gravando o vínculo errado em silêncio.
 *
 * ⚠️ O atendimento só conta como origem quando está em ENTREGA: veículo ainda em atendimento não
 * pode ser origem (o backend recusa, e a regra "1 atendimento ativo por placa" já o bloquearia).
 * Um veículo do SGA cuja placa está em atendimento aqui AGORA aparece na lista sem atendimento —
 * e o `usePlateCheck` bloqueia o cadastro logo em seguida, com a mensagem certa.
 */
function unirCandidatosDeOrigem(
  local: EventoLocal | null,
  veiculosSga: EventoVeiculo[] | null | undefined,
): CandidatoDeOrigem[] {
  const entreguesPorPlaca = new Map<string, EventoLocalCar>();
  for (const car of local?.cars ?? []) {
    if (car.status === 'ENTREGA') entreguesPorPlaca.set(placaCanonica(car.plate), car);
  }

  const candidatos: CandidatoDeOrigem[] = [];
  const jaListadas = new Set<string>();

  // A ordem da Hinova é preservada (associado primeiro) — é a mesma do cadastro com evento.
  for (const v of veiculosSga ?? []) {
    const placa = placaCanonica(v.plate);
    if (!placa || jaListadas.has(placa)) continue;
    jaListadas.add(placa);
    const atendimento = entreguesPorPlaca.get(placa) ?? null;
    candidatos.push({
      plate: placa,
      model: atendimento?.model ?? v.model ?? null,
      carClassLabel: atendimento?.carClassLabel ?? CarClassMap[v.tipo] ?? null,
      atendimento,
      veiculoSga: v,
    });
  }

  // Atendimento nosso que o SGA não devolveu (Hinova fora do ar, ou evento que ela não conhece)
  // continua sendo origem válida — é o caminho da at-96, e ele não pode depender da Hinova.
  // `Array.from` e não `for..of` direto no Map: o target do CRA é anterior a es2015 e a iteração
  // de Map exigiria `downlevelIteration` — mudar a config do projeto por causa de um laço seria
  // efeito colateral bem maior que o laço.
  for (const [placa, atendimento] of Array.from(entreguesPorPlaca.entries())) {
    if (jaListadas.has(placa)) continue;
    candidatos.push({
      plate: placa,
      model: atendimento.model ?? null,
      carClassLabel: atendimento.carClassLabel ?? null,
      atendimento,
      veiculoSga: null,
    });
  }

  return candidatos;
}

interface AddCarModalProps {
  isOpen: boolean;
  onCancel: () => void;
  /** `ehPosEvento` (at-96) evita que o checklist iniciado logo em seguida saia com o tipo errado. */
  onCarCreated?: (carId: string, ehPosEvento?: boolean) => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

/**
 * Caminhos de cadastro — decisão de negócio de 07/2026: o usuário escolhe
 * explicitamente. A at-54 unificou os dois caminhos com evento ("serviço em
 * andamento" e "criar serviço novo") em um só: a recepção não precisa mais
 * saber se o evento já está cadastrado aqui para escolher o caminho certo.
 *
 * at-96: `pos-evento` é o RETORNO DE GARANTIA — o veículo já passou pela oficina, foi entregue
 * e está voltando para avaliação do serviço. É atendimento novo, com evento próprio, vinculado
 * ao anterior.
 */
type CadastroMode = 'com-evento' | 'sem-evento' | 'pos-evento';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: CURRENT_YEAR - 1989 }, (_, i) => CURRENT_YEAR - i);

const AddCarModal: React.FC<AddCarModalProps> = ({ isOpen, onCancel, onCarCreated, sidebarRef }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // vehicle identity from FIPE
  const [vehicleType, setVehicleType] = useState<'car' | 'motorcycle'>('car');
  const [anoFabricacao, setAnoFabricacao] = useState<number | ''>('');
  const [modelYear, setModelYear] = useState<number | ''>('');
  const [selectedBrand, setSelectedBrand] = useState<FipeBrand | null>(null);
  const [selectedModel, setSelectedModel] = useState<FipeModel | null>(null);
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);

  // other car fields
  const [plate, setPlate] = useState('');
  const [chassis, setChassis] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [eventNumber, setEventNumber] = useState(0);
  const [carClass, setCarClass] = useState<CarClass | ''>('');
  const [damageType, setDamageType] = useState<DamageType | ''>('');
  const [color, setColor] = useState('');
  // A esteira inicial (AGUARDANDO) e o approvalStatus são definidos pelo backend na criação
  const [externalShopDays, setExternalShopDays] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [renavam, setRenavam] = useState('');
  // at-15: o valor FIPE que a Hinova devolveu para o veículo escolhido. Não é editável na tela — é
  // repassado como veio, com a data gravada pelo backend, e serve de piso à perda total.
  const [valorFipeSga, setValorFipeSga] = useState<number | undefined>(undefined);
  const [numeroBo, setNumeroBo] = useState('');
  const [protocol, setProtocol] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  // at-76: IMEI do rastreador — é por ele que a rastreadora localiza o equipamento.
  const [rastreadorImei, setRastreadorImei] = useState('');
  const [observacoesCliente, setObservacoesCliente] = useState('');
  const [observacoesOficina, setObservacoesOficina] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [cadastroMode, setCadastroMode] = useState<CadastroMode | null>(null);
  const [eventoLocal, setEventoLocal] = useState<EventoLocal | null>(null);
  // Veículos do evento na Hinova (at-54): undefined = ainda não buscado,
  // null = não foi possível consultar, [] = protocolo sem veículo aproveitável
  const [veiculosDoEvento, setVeiculosDoEvento] = useState<EventoVeiculo[] | null | undefined>(undefined);
  const [hinovaStatus, setHinovaStatus] = useState<HinovaStatus | undefined>(undefined);
  /** Veículo do evento já usado no pré-preenchimento — destaca o card escolhido. */
  const [veiculoSelecionadoId, setVeiculoSelecionadoId] = useState<string | null>(null);

  // ------------------------------------------------------------------ at-96: pós-evento
  //
  // 🔴 São DOIS números de evento no caminho de pós-evento, e confundi-los produz um vínculo
  // silenciosamente errado: o do serviço ORIGINAL (só para achar o atendimento de origem, nunca
  // enviado no cadastro) e o NOVO, que é o evento deste retorno.
  /** Nº do evento do serviço ORIGINAL — usado só para listar os veículos já entregues. */
  const [eventoOrigemNumero, setEventoOrigemNumero] = useState(0);
  /** Evento de origem, como o backend o devolveu. `null` = não encontrado/não consultado. */
  const [eventoOrigem, setEventoOrigem] = useState<EventoLocal | null>(null);
  /**
   * at-106 — os veículos do evento ORIGINAL no SGA.
   *
   * Até a at-96 o lado da Hinova era descartado aqui, porque o pós-evento se ancorava sempre num
   * atendimento nosso. Agora ele é a outra metade da lista: é por ele que aparece o veículo cujo
   * serviço original não está no sistema.
   */
  const [veiculosDoEventoOrigem, setVeiculosDoEventoOrigem] = useState<EventoVeiculo[] | null | undefined>(undefined);
  /** Desfecho da consulta ao SGA para o evento de ORIGEM. Ver a tabela de estados abaixo. */
  const [origemHinovaStatus, setOrigemHinovaStatus] = useState<HinovaStatus | undefined>(undefined);
  /**
   * A consulta do evento de origem falhou.
   *
   * ⚠️ Regra da at-60: "não há veículo entregue" é uma AFIRMAÇÃO, e só pode ser exibida quando
   * a consulta respondeu. O `useEventoLookup` só chama `onResult` no sucesso, então sem esta
   * flag o estado anterior (ou o vazio inicial) seria lido como resposta.
   */
  const [origemConsultaFalhou, setOrigemConsultaFalhou] = useState(false);
  /**
   * O veículo de origem escolhido (at-106).
   *
   * 🔴 Deixou de ser "o atendimento de origem": agora é um CANDIDATO, que pode ou não ter
   * atendimento nosso por trás. Com atendimento vai `posEventoDeCarId`; sem ele, vai o número do
   * evento original. Quem decide qual dos dois é o sistema, não a recepção.
   */
  const [origemEscolhida, setOrigemEscolhida] = useState<CandidatoDeOrigem | null>(null);
  /**
   * at-106 — a saída para quando o veículo não aparece na lista: SGA fora do ar, evento que ele
   * não reconhece, ou veículo que o protocolo não devolveu.
   *
   * ⚠️ Isto NÃO é a recepção declarando a origem. O lado LOCAL da resposta não depende da Hinova,
   * então "não existe atendimento entregue desta placa neste evento" continua sendo uma conclusão
   * do sistema — e o backend a reconfere pelo par (evento, placa) no salvamento, recusando o
   * cadastro se o atendimento existir. O que a recepção declara aqui é só que vai digitar os
   * dados do veículo à mão.
   */
  const [origemForaDaLista, setOrigemForaDaLista] = useState(false);

  // Pré-preenchimento automático (Hinova) já traz o tipo de veículo junto dos anos e
  // da marca/modelo — sem esta trava, o reset abaixo apagaria o que acabou de ser
  // preenchido quando o veículo é moto. Só é ligada quando o tipo realmente muda,
  // para que o reset continue valendo na troca manual do campo.
  const skipVehicleTypeResetRef = useRef(false);

  /** at-96: zera tudo do caminho de pós-evento (abertura do modal e "Trocar caminho"). */
  const limparEstadoDePosEvento = useCallback(() => {
    setEventoOrigemNumero(0);
    setEventoOrigem(null);
    setVeiculosDoEventoOrigem(undefined);
    setOrigemHinovaStatus(undefined);
    setOrigemConsultaFalhou(false);
    setOrigemEscolhida(null);
    setOrigemForaDaLista(false);
  }, []);

  // Reset FIPE selection when modal opens or vehicleType changes
  useEffect(() => {
    if (!isOpen) return;
    if (skipVehicleTypeResetRef.current) {
      skipVehicleTypeResetRef.current = false;
      return;
    }
    setModelYear('');
    setAnoFabricacao('');
    setSelectedBrand(null);
    setSelectedModel(null);
  }, [vehicleType, isOpen]);

  // Reset form fields when modal opens
  // oficina/at-136: o corpo virou função porque "Começar do zero" (rascunho restaurado) faz o mesmo.
  const limparFormulario = useCallback(() => {
      setDiscardConfirmOpen(false);
      setPlate('');
      setChassis('');
      setEntryDate('');
      setEventNumber(0);
      setCarClass('');
      setDamageType('');
      setColor('');
      setRenavam('');
      setValorFipeSga(undefined);
      setNumeroBo('');
      setProtocol('');
      setOwnerName('');
      setOwnerPhone('');
      setObservacoesCliente('');
      setObservacoesOficina('');
      setCadastroMode(null);
      setEventoLocal(null);
      setVeiculosDoEvento(undefined);
      setHinovaStatus(undefined);
      setVeiculoSelecionadoId(null);
      // at-96: o estado do pós-evento também zera na abertura — é o mesmo defeito que o
      // BUG-V89 corrigiu para o `eventoLocal`: dado velho alimenta regra e vínculo.
      limparEstadoDePosEvento();
      // at-136: IMEI e dias em oficina externa não zeravam — o cadastro seguinte herdava o IMEI do
      // veículo anterior.
      setRastreadorImei('');
      setExternalShopDays('');
  }, [limparEstadoDePosEvento]);

  useEffect(() => {
    if (isOpen) limparFormulario();
  }, [isOpen, limparFormulario]);

  // Checagem reativa de placa (bloqueio de atendimento ativo + pré-preenchimento do histórico)
  const {
    isChecking: isPlateChecking,
    isBlocked: isPlateBlocked,
    foundInHistory: plateFoundInHistory,
    activeVisitError,
  } = usePlateCheck({
    enabled: isOpen,
    plate,
    onHistoryFound: (mostRecent) => {
      notify.success('Veículo encontrado no histórico! Os campos que estavam vazios foram preenchidos.');

      // at-72: o histórico é FALLBACK do que falta, nunca autoridade sobre o que já foi
      // preenchido. Antes, `setChassis(mostRecent.chassis || '')` era atribuição
      // incondicional: uma visita antiga SEM chassi APAGAVA o chassi que o SGA acabou de
      // trazer. E o gatilho é a re-entrada de veículo, caminho normal da recepção — não
      // borda rara. A ordem também não é sorte: os dados do SGA são gravados de forma
      // síncrona e a checagem da placa resolve depois, então o histórico sempre vencia.
      //
      // A forma funcional (`(atual) => ...`) é o que garante a decisão sobre o valor
      // CORRENTE do campo, sem depender do que a closure deste callback capturou.
      setChassis((atual) => atual || mostRecent.chassis || '');
      setColor((atual) => atual || mostRecent.color || '');
      if (mostRecent.anoFabricacao) setAnoFabricacao((atual) => atual || mostRecent.anoFabricacao);
      if (mostRecent.modelYear) setModelYear((atual) => atual || mostRecent.modelYear);
      if (mostRecent.marca) {
        setSelectedBrand((atual) =>
          atual ?? ({ code: mostRecent.fipeBrandCode || '', name: mostRecent.marca } as any),
        );
      }
      if (mostRecent.model) {
        setSelectedModel((atual) =>
          atual ?? ({ code: mostRecent.fipeModelCode || '', name: mostRecent.model } as any),
        );
      }
      if (mostRecent.carClass) {
        setCarClass((atual) => atual || getCarClassKeyFromLabel(mostRecent.carClass));
      }
    },
  });

  useEffect(() => {
    if (activeVisitError) notify.warning(activeVisitError);
  }, [activeVisitError]);

  // Lookup do evento com debounce (só busca quando o usuário para de digitar).
  // at-54: um caminho só. O backend devolve o evento local (se já existir) e
  // TODOS os veículos do protocolo na Hinova — nenhum campo é preenchido
  // automaticamente: quem cadastra escolhe qual veículo está entrando.
  const { isChecking: isEventoChecking } = useEventoLookup({
    enabled: isOpen && cadastroMode === 'com-evento',
    eventNumber,
    onResult: (result: EventoLookupResult) => {
      setEventoLocal(result.local);
      setHinovaStatus(result.hinovaStatus);
      setVeiculosDoEvento(result.veiculos);

      if (result.local) {
        notify.info(`Este evento já está cadastrado com ${result.local.cars.length} veículo(s) — o novo veículo será adicionado a ele.`);
      }
      if (result.hinovaStatus === 'ok') {
        const total = result.veiculos?.length ?? 0;
        notify.success(
          `Evento encontrado no SGA com ${total} veículo(s). Selecione o que está entrando na oficina.`,
        );
      }
    },
  });

  /**
   * Lookup do evento do serviço ORIGINAL, no caminho de pós-evento (at-96, ampliado na at-106).
   *
   * Reusa o mesmo hook e a mesma rota do cadastro com evento — e, desde a at-106, aproveita a
   * resposta INTEIRA. O lado local diz quais veículos daquele evento nós atendemos e entregamos;
   * o lado da Hinova diz quais veículos o evento tem. A união dos dois é a lista de origens.
   */
  const { isChecking: isOrigemChecking } = useEventoLookup({
    enabled: isOpen && cadastroMode === 'pos-evento',
    eventNumber: eventoOrigemNumero,
    onResult: (result: EventoLookupResult) => {
      setEventoOrigem(result.local);
      setVeiculosDoEventoOrigem(result.veiculos);
      setOrigemHinovaStatus(result.hinovaStatus);
      setOrigemConsultaFalhou(false);
    },
    onError: () => {
      setEventoOrigem(null);
      setVeiculosDoEventoOrigem(undefined);
      setOrigemHinovaStatus(undefined);
      setOrigemConsultaFalhou(true);
    },
  });

  /**
   * Os veículos daquele evento que podem ser origem deste retorno (at-106).
   *
   * Cada um já vem sabendo se tem atendimento nosso — é o que o sistema responde sozinho, e é o
   * que decide se o cadastro grava o atendimento de origem ou o número do evento original.
   */
  const candidatosDeOrigem = unirCandidatosDeOrigem(eventoOrigem, veiculosDoEventoOrigem);

  // Regra "máx 1 associado por evento": bloqueia a classe ASSOCIADO quando o evento
  // já tem associado de OUTRA placa (re-entrada da mesma placa é permitida) ou no caminho sem evento.
  const normalizedPlateForRule = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const eventoJaTemOutroAssociado = Boolean(
    eventoLocal?.temAssociado &&
    !eventoLocal.cars.some((c) => c.carClass === 'ASSOCIADO' && c.plate === normalizedPlateForRule),
  );
  const associadoBloqueado = cadastroMode === 'sem-evento' || eventoJaTemOutroAssociado;

  useEffect(() => {
    if (associadoBloqueado && carClass === 'ASSOCIADO') {
      setCarClass('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associadoBloqueado]);

  // at-54: veículos do evento no SGA (associado e terceiros). Quem já tem
  // atendimento no evento é sinalizado e não é selecionável; o bloqueio real de
  // re-cadastro é a checagem de placa. O associado fica indisponível quando o
  // evento já tem o associado de outra placa (regra "máx 1 associado").
  const veiculosParaEscolher = (veiculosDoEvento ?? []).map((v) => {
    const jaCadastrado = Boolean(eventoLocal?.cars.some((c) => c.plate === v.plate));
    const associadoIndisponivel = v.tipo === CarClass.ASSOCIADO && eventoJaTemOutroAssociado;
    return {
      veiculo: v,
      jaCadastrado,
      associadoIndisponivel,
      indisponivel: jaCadastrado || associadoIndisponivel,
    };
  });

  /**
   * Preenche o formulário com os dados do veículo escolhido no evento.
   * Vale para associado e terceiro (at-54) — a classe vem do próprio item.
   */
  const handlePreencherComVeiculo = (v: EventoVeiculo) => {
    setPlate(v.plate);
    setCarClass(v.tipo);
    setVeiculoSelecionadoId(v.id);
    if (v.protocol) setProtocol(v.protocol);
    if (v.chassis) setChassis(v.chassis);
    if (v.color) setColor(v.color);
    if (v.renavam) setRenavam(v.renavam);
    setValorFipeSga(v.valorFipe);
    if (v.numeroBo) setNumeroBo(v.numeroBo);
    if (v.anoFabricacao) setAnoFabricacao(v.anoFabricacao);
    if (v.modelYear) setModelYear(v.modelYear);
    if (v.marca) setSelectedBrand({ code: '', name: v.marca } as any);
    if (v.model) setSelectedModel({ code: '', name: v.model } as any);
    if (v.ownerName) setOwnerName(v.ownerName);
    if (v.ownerPhone) setOwnerPhone(formatPhoneBr(v.ownerPhone));
    if (v.vehicleType !== vehicleType) {
      skipVehicleTypeResetRef.current = true;
      setVehicleType(v.vehicleType);
    }

    // O telefone é a chave do link público de checklist: quando vem do SGA
    // (só o associado tem), pedimos conferência; quando não vem, avisamos que
    // é digitação manual.
    notify.success(
      v.ownerPhone
        ? 'Dados preenchidos. Confira o telefone do proprietário trazido do SGA — ele é usado no link do checklist.'
        : 'Dados preenchidos. O telefone do proprietário não vem do SGA e deve ser informado manualmente.',
    );
  };

  /**
   * Escolhe o veículo de origem do pós-evento e preenche o que se sabe dele (at-96/at-106).
   *
   * Dois preenchimentos diferentes, porque as duas origens sabem coisas diferentes:
   *
   * - **Com atendimento nosso**: a placa basta. É ela que amarra o vínculo (o backend exige a
   *   MESMA placa) e é ela que dispara o `usePlateCheck`, que traz o resto do histórico do
   *   veículo do nosso próprio banco. Duplicar aqui o que aquele hook já faz seria ruído.
   * - **Sem atendimento nosso**: não existe histórico local para o hook achar, então o
   *   pré-preenchimento vem do SGA, como no cadastro com evento.
   *
   * ⚠️ O `protocol` do evento ORIGINAL não é copiado: ele pertence ao serviço anterior, e este
   * cadastro é de um atendimento novo, com evento próprio.
   */
  const handleEscolherOrigem = (origem: CandidatoDeOrigem) => {
    setOrigemEscolhida(origem);
    setPlate(origem.plate);

    const { atendimento, veiculoSga } = origem;

    if (atendimento) {
      if (atendimento.carClass) {
        setCarClass(getCarClassKeyFromLabel(atendimento.carClassLabel) || (atendimento.carClass as CarClass));
      }
      notify.success(
        'Atendimento de origem selecionado. Informe o número do evento NOVO, aberto para este retorno.',
      );
      return;
    }

    if (veiculoSga) {
      setCarClass(veiculoSga.tipo);
      if (veiculoSga.chassis) setChassis(veiculoSga.chassis);
      if (veiculoSga.color) setColor(veiculoSga.color);
      if (veiculoSga.renavam) setRenavam(veiculoSga.renavam);
      setValorFipeSga(veiculoSga.valorFipe);
      if (veiculoSga.anoFabricacao) setAnoFabricacao(veiculoSga.anoFabricacao);
      if (veiculoSga.modelYear) setModelYear(veiculoSga.modelYear);
      if (veiculoSga.marca) setSelectedBrand({ code: '', name: veiculoSga.marca } as any);
      if (veiculoSga.model) setSelectedModel({ code: '', name: veiculoSga.model } as any);
      if (veiculoSga.ownerName) setOwnerName(veiculoSga.ownerName);
      if (veiculoSga.ownerPhone) setOwnerPhone(formatPhoneBr(veiculoSga.ownerPhone));
      if (veiculoSga.vehicleType !== vehicleType) {
        skipVehicleTypeResetRef.current = true;
        setVehicleType(veiculoSga.vehicleType);
      }
    }

    notify.success(
      'Veículo selecionado. O serviço original não está no sistema, então ele fica registrado pelo ' +
      'número do evento. Informe o número do evento NOVO, aberto para este retorno.',
    );
  };

  // Ano do modelo pode ser o mesmo ano de fabricação ou o ano seguinte
  const compatibleModelYears: number[] =
    anoFabricacao === ''
      ? []
      : [anoFabricacao as number, (anoFabricacao as number) + 1].filter((y) => y <= CURRENT_YEAR + 1);

  // Reset downstream when years change
  const handleAnoFabricacaoChange = (val: number | '') => {
    setAnoFabricacao(val);
    setModelYear('');
    setSelectedBrand(null);
    setSelectedModel(null);
  };

  const handleModelYearChange = (val: number | '') => {
    setModelYear(val);
    setSelectedBrand(null);
    setSelectedModel(null);
  };

  const yearsReady = anoFabricacao !== '' && modelYear !== '';
  const brandReady = yearsReady && selectedBrand !== null;

  // Brand picker fetch function (memoised via useCallback)
  const fetchBrands = useCallback(
    ({ search, page, limit }: { search?: string; page: number; limit: number }) =>
      fetchFipeBrands({ type: vehicleType, modelYear: modelYear as number, search, page, limit }) as unknown as Promise<{
        totalItems: number;
        items: PickerItem[];
      }>,
    [vehicleType, modelYear],
  );

  // Model picker fetch function
  const fetchModels = useCallback(
    ({ search, page, limit }: { search?: string; page: number; limit: number }) =>
      fetchFipeModels({
        brandCode: selectedBrand?.code,
        type: vehicleType,
        modelYear: modelYear as number,
        search,
        page,
        limit,
      }) as unknown as Promise<{ totalItems: number; items: PickerItem[] }>,
    [selectedBrand, vehicleType, modelYear],
  );

  const handleBrandSelect = (item: PickerItem) => {
    setSelectedBrand(item as unknown as FipeBrand);
    setSelectedModel(null);
    setShowBrandPicker(false);
  };

  const handleModelSelect = (item: PickerItem) => {
    setSelectedModel(item as unknown as FipeModel);
    setShowModelPicker(false);
  };


  // at-134: drawer "Como Funciona" do cadastro.
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  useEffect(() => {
    if (!isOpen) setIsHelpOpen(false);
  }, [isOpen]);

  // Ref para evitar closure stale ao checar se picker/confirmação está aberto.
  // 🔴 `isHelpOpen` entra aqui (at-134): o drawer é renderizado FORA do `modalRef`, então sem
  // isto o clique nele conta como "clicou fora" e abre a confirmação de descarte do cadastro
  // (mesma armadilha do ChecklistModal, at-58).
  const anyPickerOpenRef = useRef(false);
  useEffect(() => {
    anyPickerOpenRef.current = showBrandPicker || showModelPicker || discardConfirmOpen || isHelpOpen;
  }, [showBrandPicker, showModelPicker, discardConfirmOpen, isHelpOpen]);

  // Formulário com dados digitados — fechar exige confirmação para não perder o cadastro
  const isDirty = Boolean(
    plate ||
    chassis ||
    entryDate ||
    eventNumber !== 0 ||
    carClass ||
    damageType ||
    color ||
    renavam ||
    numeroBo ||
    ownerName ||
    ownerPhone ||
    observacoesCliente ||
    observacoesOficina ||
    externalShopDays ||
    anoFabricacao !== '' ||
    selectedBrand ||
    selectedModel ||
    // at-96: sem estes, fechar o modal com uma origem já escolhida descartaria a escolha sem
    // nenhuma confirmação.
    eventoOrigemNumero !== 0 ||
    origemEscolhida,
  );
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // ------------------------------------------------------------------ oficina/at-136: rascunho
  //
  // A versão nova do sistema recarrega a página sozinha; o cadastro em andamento sobrevive porque
  // é guardado no servidor enquanto a pessoa preenche. Ficam de fora as listas que vêm do SGA
  // (o lookup do evento as refaz sozinho a partir do número) — o que volta é o que foi ESCOLHIDO.
  const rascunho = useRascunho<Record<string, unknown>>(CHAVE_CADASTRO_VEICULO, isOpen);
  const [rascunhoRestauradoEm, setRascunhoRestauradoEm] = useState<string | null>(null);

  const conteudoDoRascunho = {
    cadastroMode,
    vehicleType,
    anoFabricacao,
    modelYear,
    selectedBrand,
    selectedModel,
    plate,
    chassis,
    entryDate,
    eventNumber,
    carClass,
    damageType,
    color,
    externalShopDays,
    renavam,
    valorFipeSga: valorFipeSga ?? null,
    numeroBo,
    protocol,
    ownerName,
    ownerPhone,
    rastreadorImei,
    observacoesCliente,
    observacoesOficina,
    veiculoSelecionadoId,
    eventoOrigemNumero,
    origemEscolhida,
    origemForaDaLista,
  };
  const conteudoDoRascunhoJson = JSON.stringify(conteudoDoRascunho);

  useEffect(() => {
    if (!isOpen) setRascunhoRestauradoEm(null);
  }, [isOpen]);

  // Restaura uma vez, quando a leitura responde com rascunho.
  useEffect(() => {
    const r = rascunho.inicial as any;
    if (rascunho.estado !== 'pronto' || !r) return;
    if (r.vehicleType && r.vehicleType !== vehicleType) {
      skipVehicleTypeResetRef.current = true;
      setVehicleType(r.vehicleType);
    }
    setCadastroMode(r.cadastroMode ?? null);
    setAnoFabricacao(r.anoFabricacao ?? '');
    setModelYear(r.modelYear ?? '');
    setSelectedBrand(r.selectedBrand ?? null);
    setSelectedModel(r.selectedModel ?? null);
    setPlate(r.plate ?? '');
    setChassis(r.chassis ?? '');
    setEntryDate(r.entryDate ?? '');
    setEventNumber(r.eventNumber ?? 0);
    setCarClass(r.carClass ?? '');
    setDamageType(r.damageType ?? '');
    setColor(r.color ?? '');
    setExternalShopDays(r.externalShopDays ?? '');
    setRenavam(r.renavam ?? '');
    setValorFipeSga(r.valorFipeSga ?? undefined);
    setNumeroBo(r.numeroBo ?? '');
    setProtocol(r.protocol ?? '');
    setOwnerName(r.ownerName ?? '');
    setOwnerPhone(r.ownerPhone ?? '');
    setRastreadorImei(r.rastreadorImei ?? '');
    setObservacoesCliente(r.observacoesCliente ?? '');
    setObservacoesOficina(r.observacoesOficina ?? '');
    setVeiculoSelecionadoId(r.veiculoSelecionadoId ?? null);
    setEventoOrigemNumero(r.eventoOrigemNumero ?? 0);
    setOrigemEscolhida(r.origemEscolhida ?? null);
    setOrigemForaDaLista(Boolean(r.origemForaDaLista));
    setRascunhoRestauradoEm(rascunho.atualizadoEm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rascunho.estado, rascunho.inicial]);

  // Guarda enquanto preenche. Formulário vazio não vira rascunho.
  useEffect(() => {
    if (!isOpen || !isDirty) return;
    rascunho.agendar(JSON.parse(conteudoDoRascunhoJson));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDirty, conteudoDoRascunhoJson]);

  const comecarDoZero = useCallback(() => {
    void rascunho.descartar();
    setRascunhoRestauradoEm(null);
    limparFormulario();
  }, [rascunho, limparFormulario]);

  const requestClose = useCallback(() => {
    if (isDirtyRef.current) {
      setDiscardConfirmOpen(true);
      return;
    }
    onCancel();
  }, [onCancel]);

  // click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (anyPickerOpenRef.current) return;
      const node = e.target as Node;
      if (modalRef.current && !modalRef.current.contains(node) && !sidebarRef?.current?.contains(node)) {
        requestClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, requestClose, sidebarRef]);

  const ehPosEvento = cadastroMode === 'pos-evento';
  // at-96: o pós-evento SEMPRE tem evento próprio (é evento SGA normal), então o caminho dele
  // compartilha tudo o que depende de "tem número de evento".
  const comEvento = cadastroMode === 'com-evento' || ehPosEvento;

  const handleSaveCar = async () => {
    setIsLoading(true);
    try {
      // Só placa e classe são obrigatórias (+ evento para ASSOCIADO) — decisão de 07/2026
      if (!plate.trim()) { notify.warning('Por favor, informe a placa do veículo.'); setIsLoading(false); return; }
      if (!carClass) { notify.warning('Por favor, selecione a classe do veículo.'); setIsLoading(false); return; }
      if (comEvento && eventNumber < 1) { notify.warning('Por favor, informe o número do evento.'); setIsLoading(false); return; }
      if (!comEvento && carClass === 'ASSOCIADO') { notify.warning('Veículo de associado exige um evento.'); setIsLoading(false); return; }

      if (ehPosEvento) {
        if (!origemEscolhida && !origemForaDaLista) {
          notify.warning('Selecione o veículo de origem — o serviço que este veículo está voltando para avaliar.');
          setIsLoading(false);
          return;
        }
        if (eventoOrigemNumero < 1) {
          notify.warning('Informe o número do evento do serviço original.');
          setIsLoading(false);
          return;
        }
        // 🔴 O erro mais provável deste fluxo: digitar de novo o número do evento original no
        // campo do evento novo. O backend recusaria por outra via (`eventNumber` é único, então
        // a mensagem falaria de "evento já cadastrado") e o motivo real ficaria escondido.
        if (eventNumber === eventoOrigemNumero) {
          notify.warning(
            'O número do evento deste pós-evento tem de ser diferente do evento do serviço original. Informe o evento NOVO, aberto para este retorno.',
          );
          setIsLoading(false);
          return;
        }
      }
      const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');
      if (ownerPhoneDigits && (ownerPhoneDigits.length < 10 || ownerPhoneDigits.length > 13)) {
        notify.warning('O telefone do proprietário deve conter DDD e número (10 a 13 dígitos).');
        setIsLoading(false);
        return;
      }
      // Mesmo padrão do telefone: opcional, mas se preenchido tem de ter comprimento válido —
      // o DTO do backend recusa com 400, e é melhor barrar aqui com texto claro (at-76).
      const imeiDigits = rastreadorImei.replace(/\D/g, '');
      if (imeiDigits && (imeiDigits.length < 14 || imeiDigits.length > 17)) {
        notify.warning('O IMEI do rastreador deve ter de 14 a 17 dígitos.');
        setIsLoading(false);
        return;
      }
      // Tipo/tamanho já validados (e convertidos) na seleção; sem limite de quantidade

      const carData = {
        plate,
        chassis: chassis || undefined,
        model: selectedModel?.name,
        entryDate: entryDate ? new Date(entryDate).toISOString() : undefined,
        eventNumber: comEvento ? eventNumber : undefined,
        protocol: comEvento && protocol ? protocol : undefined,
        carClass,
        damageType: damageType || undefined,
        color: color || undefined,
        ownerName: ownerName.trim() || undefined,
        ownerPhone: ownerPhoneDigits || undefined,
        rastreadorImei: imeiDigits || undefined,
        observacoesCliente: observacoesCliente.trim() || undefined,
        observacoesOficina: observacoesOficina.trim() || undefined,
        externalShopDays: externalShopDays !== '' ? Number(externalShopDays) : undefined,
        modelYear: modelYear !== '' ? Number(modelYear) : undefined,
        anoFabricacao: anoFabricacao !== '' ? Number(anoFabricacao) : undefined,
        marca: selectedBrand?.name,
        fipeBrandCode: selectedBrand?.code || undefined,
        fipeModelCode: (selectedModel as any)?.code || undefined,
        renavam: renavam || undefined,
        valorFipeSga,
        numeroBo: numeroBo || undefined,
        // at-96/at-106: o vínculo com o serviço original. Só existe no caminho de pós-evento, e
        // 🔴 os dois campos são MUTUAMENTE EXCLUSIVOS — o backend recusa os dois juntos, porque
        // seriam duas respostas para "qual serviço este retorno avalia". Quem escolhe é o
        // sistema, a partir do que ele sabe do veículo, nunca a recepção.
        posEventoDeCarId: ehPosEvento ? origemEscolhida?.atendimento?.id : undefined,
        posEventoDeEventoOrigemNumero:
          ehPosEvento && !origemEscolhida?.atendimento ? eventoOrigemNumero : undefined,
      };

      const createdCar = await createCar(carData);
      // Só depois de o cadastro existir: descartar antes perderia o rascunho de um cadastro que falhou.
      await rascunho.descartar();

      notify.success(
        !ehPosEvento
          ? 'Veículo adicionado com sucesso'
          : origemEscolhida?.atendimento
            ? 'Pós-evento cadastrado e vinculado ao atendimento do serviço original.'
            : `Pós-evento cadastrado e vinculado ao evento ${eventoOrigemNumero}. O serviço original não está no sistema, então não há vistoria anterior para comparar.`,
      );
      setTimeout(() => {
        setIsLoading(false);
        if (onCarCreated) { onCarCreated(createdCar.id, ehPosEvento); return; }
        onCancel();
      }, 300);
    } catch (error) {
      // O motivo do backend (placa já em atendimento, evento já com associado) é o que orienta
      // quem está cadastrando — por isso `errorFrom`, que o prioriza.
      notify.errorFrom(error, 'Não foi possível cadastrar o veículo. Tente novamente; se persistir, acione a TI.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // at-134: ajuda do cadastro. O conteúdo sai das regras já escritas (04-business-rules.md,
  // features/cadastro-de-veiculo-e-eventos.md e features/pos-evento.md) — nunca de regra nova.
  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'Os três caminhos',
      conteudo: (
        <>
          <p>
            <strong>Cadastrar veículo com evento</strong>: o veículo chega por um evento aberto no SGA. É o caminho
            normal do associado e serve também para os terceiros do mesmo evento.
          </p>
          <p>
            <strong>Cadastrar veículo sem evento</strong>: para quem entra sem evento (um terceiro, por exemplo). Ele
            pode ser vinculado a um evento depois, pela edição do veículo.
          </p>
          <p>
            <strong>Cadastrar pós-evento (retorno de garantia)</strong>: o veículo já foi entregue e voltou para
            avaliar o serviço. É um atendimento novo, com evento próprio, ligado ao serviço original.
          </p>
        </>
      ),
    },
    {
      titulo: 'Com evento: você escolhe o veículo',
      conteudo: (
        <>
          <p>
            Ao informar o número do evento, o sistema busca no SGA <strong>todos os veículos do evento</strong>: o do
            associado e os de terceiros. Você clica no veículo que está entrando e o formulário é preenchido com os
            dados dele, inclusive a classe. Nenhum campo é preenchido sem você escolher.
          </p>
          <p>
            Serve para o primeiro veículo do evento e para os seguintes, em qualquer ordem. Um veículo que já tem
            atendimento neste evento aparece sinalizado e não pode ser escolhido.
          </p>
          <p>
            Cada evento aceita <strong>um único veículo de associado</strong>. Terceiros são ilimitados. Veículo de
            associado sempre exige evento.
          </p>
        </>
      ),
    },
    {
      titulo: 'Quando o SGA não responde',
      conteudo: (
        <>
          <p>
            A consulta ao SGA nunca impede o cadastro. Se o evento não for encontrado, ou se o SGA estiver fora do
            ar, a tela avisa e você preenche os dados à mão. O evento é registrado do mesmo jeito ao salvar.
          </p>
          <p>
            Evento que já existe aqui aparece mesmo com o SGA fora, com os veículos que já foram cadastrados nele.
          </p>
        </>
      ),
    },
    {
      titulo: 'Pós-evento: dois números de evento',
      conteudo: (
        <>
          <p>
            O pós-evento tem <strong>dois números</strong>, e confundi-los grava o vínculo errado. O primeiro é o do{' '}
            <strong>serviço original</strong>, usado só para achar o veículo que voltou. O segundo é o do{' '}
            <strong>evento novo</strong>, aberto no SGA para este retorno. Os dois precisam ser diferentes.
          </p>
          <p>
            Você escolhe o veículo; o sistema decide se o serviço original foi feito aqui. Se foi, o retorno fica
            ligado àquele atendimento e a vistoria de entrada pode ser comparada com a de saída. Se não foi (oficina
            externa, ou antes do sistema), o selo passa a ser "Pós-evento externo" e não há vistoria anterior.
          </p>
          <p>
            A garantia de 90 dias é informativa: o sistema mostra há quantos dias o veículo foi entregue, mas não
            impede o cadastro.
          </p>
        </>
      ),
    },
    {
      titulo: 'Placa e atendimento ativo',
      conteudo: (
        <>
          <p>
            Só <strong>Placa</strong> e <strong>Classe do Veículo</strong> são obrigatórias (mais o evento, para
            associado). Uma placa só pode ter <strong>um atendimento em andamento</strong>: se o veículo ainda não
            foi entregue, o cadastro é recusado.
          </p>
          <p>
            Placa que já passou pela oficina tem os campos vazios preenchidos a partir do histórico.
          </p>
        </>
      ),
    },
    {
      titulo: 'Telefone e observações',
      conteudo: (
        <>
          <p>
            O <strong>Telefone do proprietário</strong> é a chave dos links que o associado recebe (checklist e
            pesquisa de satisfação): ele confirma o número para abrir. Sem telefone, esses envios ficam
            indisponíveis. Para o associado, o número vem do SGA e precisa ser conferido; para terceiro, é digitado.
          </p>
          <p>
            <strong>Observações do Cliente</strong> é o que o cliente relatou ou pediu, e{' '}
            <strong>Observações da Oficina</strong> são anotações da equipe. As duas são internas: não saem no PDF do
            checklist nem na tela do associado.
          </p>
        </>
      ),
    },
    {
      titulo: 'Rastreador e oficina externa',
      conteudo: (
        <>
          <p>
            O <strong>IMEI do rastreador</strong> é opcional: a equipe de rastreamento consegue consultar pela placa,
            e o IMEI é preenchido sozinho na primeira consulta.
          </p>
          <p>
            <strong>Dias em Oficina Externa</strong> descontam do prazo de 90 dias do veículo na oficina.
          </p>
        </>
      ),
    },
    {
      titulo: 'Se a tela recarregar',
      conteudo: (
        <>
          <p>
            Quando sai uma versão nova do sistema, a tela se atualiza sozinha. O que você estava preenchendo é
            guardado enquanto você digita e volta quando você abrir o cadastro de novo, com o aviso de que foi
            recuperado.
          </p>
          <p>
            <strong>Começar do zero</strong> apaga o que foi recuperado. Salvar o veículo ou descartar o cadastro
            também apaga o rascunho.
          </p>
        </>
      ),
    },
  ];

  const botaoAjuda = (
    <button
      type="button"
      className="help-circle-btn"
      title="Como Funciona"
      aria-label="Como funciona o cadastro de veículo"
      onClick={() => setIsHelpOpen(true)}
    >
      ?
    </button>
  );

  return (
    <div className="add-car-modal-overlay">
      {isLoading && (
        <FullScreenLoader message="Salvando veículo..." />
      )}
      <div className="edit-car-modal-content" ref={modalRef}>
        <div className="Lightbox">
          {cadastroMode === null ? (
            <div className="LightboxLogin">
              <div className="EditarCarro" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Adicionar veículo
                {botaoAjuda}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', padding: '12px 0' }}>
                <div className="CampoTitulo" style={{ fontSize: 14, fontWeight: 600, color: 'var(--tema-texto-2, #475569)', marginBottom: 4 }}>Como deseja cadastrar?</div>
                <button
                  type="button"
                  className="cadastro-opcao-card"
                  onClick={() => setCadastroMode('com-evento')}
                >
                  <div className="cadastro-opcao-title">Cadastrar veículo com evento</div>
                  <div className="cadastro-opcao-desc">
                    Digite o nº do evento aberto no SGA para ver todos os veículos envolvidos (associado e terceiros) e escolher o que está entrando na oficina. Serve tanto para o primeiro veículo do evento quanto para os seguintes, em qualquer ordem.
                  </div>
                </button>
                <button
                  type="button"
                  className="cadastro-opcao-card"
                  onClick={() => setCadastroMode('sem-evento')}
                >
                  <div className="cadastro-opcao-title">Cadastrar veículo sem evento</div>
                  <div className="cadastro-opcao-desc">
                    Para veículos que não têm nº de evento (ex: terceiro). O serviço segue normalmente e é possível vincular o evento depois, pela edição.
                  </div>
                </button>
                {/*
                  at-96 — o retorno de garantia.
                  ⚠️ A descrição explica a REGRA, não o clique, e NÃO promete validação de prazo:
                  o sistema não valida os 90 dias (decisão do Victor: só informativo).
                */}
                <button
                  type="button"
                  className="cadastro-opcao-card"
                  onClick={() => setCadastroMode('pos-evento')}
                >
                  <div className="cadastro-opcao-title">Cadastrar pós-evento (retorno de garantia)</div>
                  <div className="cadastro-opcao-desc">
                    Para o veículo que está voltando para avaliação ou correção de um serviço anterior. É um atendimento novo, com número de evento próprio, aberto no SGA — e fica vinculado ao serviço original. Informe o evento do serviço original e escolha o veículo: o sistema identifica sozinho se aquele serviço foi feito aqui, e serve também para o serviço que não passou por esta oficina.
                  </div>
                </button>
              </div>
              <div className="Frame1000003998" style={{ marginTop: 24 }}>
                <div className="CtaBigEditar cancel" onClick={onCancel}>
                  <button className="CustomFileCancel" type="button">Cancelar</button>
                </div>
              </div>
            </div>
          ) : (
          <form className="LightboxLogin" onSubmit={(e) => { e.preventDefault(); handleSaveCar(); }}>
            <div className="EditarCarro" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Adicionar veículo
              {botaoAjuda}
            </div>
            {rascunhoRestauradoEm !== null && (
              <RascunhoRestaurado atualizadoEm={rascunhoRestauradoEm} onComecarDoZero={comecarDoZero} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', fontSize: 12 }}>
              <span style={{ color: 'var(--tema-texto-2, #666)' }}>
                {cadastroMode === 'com-evento' && 'Cadastro com evento'}
                {cadastroMode === 'sem-evento' && 'Cadastro sem evento'}
                {cadastroMode === 'pos-evento' && 'Cadastro de pós-evento (retorno de garantia)'}
              </span>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: '#ea5d0e', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}
                onClick={() => {
                  setCadastroMode(null);
                  setEventoLocal(null);
                  setVeiculosDoEvento(undefined);
                  setHinovaStatus(undefined);
                  setVeiculoSelecionadoId(null);
                  setEventNumber(0);
                  setProtocol('');
                  // at-96: trocar de caminho não pode deixar o vínculo do pós-evento para trás.
                  limparEstadoDePosEvento();
                }}
              >
                Trocar caminho
              </button>
            </div>
            <div className="Frame1000004010">

              {/*
                at-96 — passo 1 do pós-evento: achar o serviço original.
                🔴 São DOIS números de evento, e os rótulos precisam deixar isso inequívoco:
                confundi-los produz um vínculo silenciosamente errado.
              */}
              {ehPosEvento && (
                <div className="pos-evento-origem">
                  <div className="Campo">
                    <div className="CampoTitulo">
                      Nº do evento do serviço original <span className="required-asterisk">*</span>
                    </div>
                    <div className="DropDownFormulRio">
                      <input
                        className="Input"
                        type="text"
                        value={eventoOrigemNumero === 0 ? '' : eventoOrigemNumero}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setEventoOrigemNumero(e.target.value === '' ? 0 : Number(e.target.value));
                            // Dado velho alimenta regra (BUG-V89): a lista de origens e a escolha
                            // saem junto com o número que as produziu. ⚠️ at-106: isso passou a
                            // incluir os veículos do SGA e o desfecho da consulta — mantidos, a
                            // tela decidiria "está no sistema?" sobre o evento ANTERIOR.
                            setEventoOrigem(null);
                            setVeiculosDoEventoOrigem(undefined);
                            setOrigemHinovaStatus(undefined);
                            setOrigemConsultaFalhou(false);
                            setOrigemEscolhida(null);
                            setOrigemForaDaLista(false);
                          }
                        }}
                      />
                    </div>
                    <span className="pos-evento-hint">
                      É o evento do serviço que este veículo está voltando para avaliar. O serviço
                      pode ter sido feito aqui ou não — o sistema identifica sozinho. O evento
                      DESTE retorno é outro, informado logo abaixo.
                    </span>
                    {isOrigemChecking && <span className="plate-status-msg checking">Buscando evento...</span>}
                  </div>

                  {/*
                    ⚠️ at-60 em dobro: "este evento não tem veículo entregue" é uma AFIRMAÇÃO, e só
                    aparece quando a consulta RESPONDEU. Falha de consulta tem texto próprio.
                  */}
                  {!isOrigemChecking && origemConsultaFalhou && (
                    <div className="erro-de-tela">
                      Não foi possível consultar este evento. Sem a consulta não dá para saber se ele
                      tem veículo entregue — tente novamente; se persistir, acione a TI.
                    </div>
                  )}

                  {/*
                    at-106: o SGA não respondeu. A lista pode existir mesmo assim (o lado local não
                    depende da Hinova), então isto é aviso, nunca bloqueio.
                  */}
                  {!isOrigemChecking && !origemConsultaFalhou && origemHinovaStatus === 'indisponivel' && (
                    <div className="pos-evento-aviso">
                      Não foi possível consultar o SGA agora, então a lista abaixo pode estar
                      incompleta. Os veículos que já passaram pela oficina neste evento continuam
                      aparecendo.
                    </div>
                  )}

                  {!isOrigemChecking && !origemConsultaFalhou && candidatosDeOrigem.length === 0 && (
                    <div className="pos-evento-aviso">
                      {origemHinovaStatus === 'nao-encontrado'
                        ? 'Este evento não foi encontrado no SGA e não tem veículo entregue na oficina.'
                        : 'Nenhum veículo foi encontrado para este evento.'}
                    </div>
                  )}

                  {candidatosDeOrigem.length > 0 && (
                    <div className="terceiros-hinova">
                      <div className="terceiros-hinova-title">
                        Veículos deste evento ({candidatosDeOrigem.length}):
                      </div>
                      <div className="terceiros-hinova-hint">
                        Escolha o veículo que está voltando. O sistema identifica sozinho se o
                        serviço dele foi feito aqui.
                      </div>
                      {candidatosDeOrigem.map((c) => (
                        <button
                          key={c.plate}
                          type="button"
                          className={`terceiro-card${origemEscolhida?.plate === c.plate ? ' terceiro-card-selecionado' : ''}`}
                          onClick={() => handleEscolherOrigem(c)}
                        >
                          <div className="terceiro-card-linha">
                            <span className="terceiro-card-placa">{c.plate}</span>
                            <span>{c.model ?? 'Modelo não informado'}</span>
                            <span className="terceiro-card-classe">{c.carClassLabel}</span>
                            {origemEscolhida?.plate === c.plate && (
                              <span className="terceiro-card-badge terceiro-card-badge-ok">selecionado</span>
                            )}
                          </div>
                          <div className="terceiro-card-linha terceiro-card-meta">
                            {/*
                              🔴 at-106: a linha que diz o que o sistema descobriu sobre ESTE
                              veículo. O texto do caso sem atendimento para no que se sabe — "não
                              está no sistema" —, nunca "foi feito em oficina externa": a causa
                              (oficina externa, serviço anterior ao go-live, cadastro que ninguém
                              fez) não é conhecida, e afirmá-la seria inventar.
                              A data é só informativa: o sistema não valida prazo de garantia.
                            */}
                            {c.atendimento ? (
                              <span>
                                Já passou pela oficina —{' '}
                                {c.atendimento.entregueEm
                                  ? `entregue em ${new Date(c.atendimento.entregueEm).toLocaleDateString('pt-BR')}` +
                                    (typeof c.atendimento.diasDesdeEntrega === 'number'
                                      ? ` (há ${c.atendimento.diasDesdeEntrega} ${c.atendimento.diasDesdeEntrega === 1 ? 'dia' : 'dias'})`
                                      : '')
                                  : 'data de entrega não registrada'}
                              </span>
                            ) : (
                              <span>O serviço deste veículo não está no sistema</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/*
                    at-106 — a saída para o veículo que não aparece na lista (SGA fora do ar, ou
                    evento que ele não reconhece). Só é oferecida quando a consulta RESPONDEU:
                    depois de uma falha, "não está na lista" seria conclusão tirada de um erro de
                    rede, e o vínculo gravado é imutável.
                  */}
                  {!isOrigemChecking && !origemConsultaFalhou && eventoOrigemNumero > 0 && !origemEscolhida && (
                    origemForaDaLista ? (
                      <div className="pos-evento-aviso">
                        Este retorno vai ficar vinculado ao evento {eventoOrigemNumero}, sem
                        atendimento de origem — preencha os dados do veículo abaixo.{' '}
                        <button
                          type="button"
                          className="pos-evento-link"
                          onClick={() => setOrigemForaDaLista(false)}
                        >
                          Voltar e escolher na lista
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="pos-evento-link"
                        onClick={() => setOrigemForaDaLista(true)}
                      >
                        O veículo não está na lista — informar os dados dele à mão
                      </button>
                    )
                  )}
                </div>
              )}

              {/* Row 1: Evento, Chassi, Placa */}
              <div className="Frame1000003994">
                {comEvento && (
                <div className="Campo">
                  <div className="CampoTitulo">
                    {ehPosEvento ? 'Nº do evento deste pós-evento' : 'Número do Evento'}{' '}
                    <span className="required-asterisk">*</span>
                  </div>
                  <div className="DropDownFormulRio">
                    <input
                      className="Input"
                      type="text"
                      value={eventNumber === 0 ? '' : eventNumber}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          setEventNumber(e.target.value === '' ? 0 : Number(e.target.value));
                          // evita oferecer os veículos do número anterior enquanto a nova busca não volta
                          setVeiculosDoEvento(undefined);
                          setHinovaStatus(undefined);
                          setVeiculoSelecionadoId(null);
                          // at-72 (BUG-V89): o `eventoLocal` também tem de sair daqui, e isso
                          // NÃO é cosmético — ele alimenta a regra "máx. 1 associado por
                          // evento", que bloqueia a opção ASSOCIADO e chega a ZERAR o
                          // `carClass` já escolhido. Mantido, a tela decide sobre o evento
                          // ANTERIOR; e se a consulta falhar, o `onResult` nunca é chamado e o
                          // dado velho fica indefinidamente, não só durante o debounce.
                          // O painel piscar entre buscas é preferível a exibir evento errado.
                          setEventoLocal(null);
                        }
                      }}
                      required
                    />
                  </div>
                  {ehPosEvento && (
                    <span className="pos-evento-hint">
                      É o evento NOVO, aberto no SGA para este retorno — não repita o número do
                      serviço original.
                    </span>
                  )}
                  {isEventoChecking && <span className="plate-status-msg checking">Buscando evento...</span>}
                  {/* at-54: nenhum dos dois casos bloqueia o cadastro — o veículo entra
                      com os dados digitados à mão e o evento é criado no salvamento. */}
                  {!isEventoChecking && hinovaStatus === 'nao-encontrado' && (
                    <span className="plate-status-msg error">
                      Este número de evento não foi encontrado no SGA. Confira o número; se estiver certo, preencha os dados do veículo manualmente.
                    </span>
                  )}
                  {!isEventoChecking && hinovaStatus === 'indisponivel' && (
                    <span className="plate-status-msg error">
                      Não foi possível consultar o SGA agora. Os dados do veículo podem ser preenchidos manualmente.
                    </span>
                  )}
                </div>
                )}
                <div className="Campo">
                  <div className="CampoTitulo">Chassi</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" value={chassis} onChange={(e) => setChassis(e.target.value)} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Placa <span className="required-asterisk">*</span></div>
                  <div className="CampoInput">
                    <input className="Input" type="text" value={plate} onChange={(e) => setPlate(e.target.value)} required />
                  </div>
                  {isPlateChecking && <span className="plate-status-msg checking">Verificando placa...</span>}
                  {activeVisitError && <span className="plate-status-msg error">{activeVisitError}</span>}
                  {plateFoundInHistory && <span className="plate-status-msg success">Veículo no histórico! Dados preenchidos.</span>}
                </div>
              </div>

              {/* Painel do evento (at-54): o que já está na oficina + os veículos do SGA para escolher */}
              {comEvento && (eventoLocal || veiculosParaEscolher.length > 0) && (
                <div className="evento-painel">
                  {eventoLocal && (
                    <>
                      <div className="evento-painel-title">
                        Evento {eventoLocal.eventNumber} já cadastrado — {eventoLocal.cars.length} veículo(s) na oficina:
                      </div>
                      {eventoLocal.cars.map((c) => (
                        <div key={c.id} className="evento-painel-car">
                          <span className="evento-painel-car-placa">{c.plate}</span>
                          <span className="evento-painel-car-modelo">{c.model}</span>
                          <span className="evento-painel-car-classe">{c.carClassLabel}</span>
                          <span className="evento-painel-car-status">{c.statusLabel ?? '-'}</span>
                        </div>
                      ))}
                      {eventoJaTemOutroAssociado && (
                        <div className="evento-painel-aviso">
                          Este evento já possui o veículo do associado — só é possível adicionar terceiros.
                        </div>
                      )}
                      {/*
                        at-96 — o facilitador.
                        🔴 A condição é "o evento já tem veículo ENTREGUE", NUNCA "o evento
                        existe": digitar evento existente é o caminho normal do segundo veículo
                        do sinistro (medido em produção: 2 dos 66 eventos têm 2 veículos), e
                        perguntar ali seria atrito em cima do fluxo comum.
                        ⚠️ É aviso com ação, não bloqueio — e não é toast, que sumiria antes de a
                        pessoa decidir e não comporta botão.
                      */}
                      {eventoLocal.cars.some((c) => c.status === 'ENTREGA') && (
                        <div className="pos-evento-facilitador">
                          <div className="pos-evento-facilitador-texto">
                            Este evento já tem veículo entregue. Se o veículo está voltando para
                            avaliação do serviço, o cadastro correto é o de pós-evento (retorno de
                            garantia) — ele abre um atendimento novo, com evento próprio, vinculado
                            ao serviço original.
                          </div>
                          <button
                            type="button"
                            className="pos-evento-facilitador-acao"
                            onClick={() => {
                              // O número digitado vira o evento de ORIGEM — é o que ele é.
                              const numeroDigitado = eventNumber;
                              setCadastroMode('pos-evento');
                              setEventoOrigemNumero(numeroDigitado);
                              setEventoOrigem(eventoLocal);
                              setOrigemConsultaFalhou(false);
                              setOrigemEscolhida(null);
                              // O campo do evento novo nasce vazio: o número antigo aqui é
                              // justamente o engano que o fluxo precisa evitar.
                              setEventNumber(0);
                              setEventoLocal(null);
                              setVeiculosDoEvento(undefined);
                              setHinovaStatus(undefined);
                              setVeiculoSelecionadoId(null);
                            }}
                          >
                            É um pós-evento — mudar para esse cadastro
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {veiculosParaEscolher.length > 0 && (
                    <div className="terceiros-hinova">
                      <div className="terceiros-hinova-title">
                        Veículos deste evento no SGA ({veiculosParaEscolher.length}):
                      </div>
                      <div className="terceiros-hinova-hint">
                        Selecione o veículo que está entrando na oficina para preencher o cadastro. Nenhum dado é preenchido automaticamente antes da sua escolha.
                      </div>
                      {veiculosParaEscolher.map(({ veiculo: v, jaCadastrado, associadoIndisponivel, indisponivel }) => (
                        <button
                          key={v.id}
                          type="button"
                          className={`terceiro-card${veiculoSelecionadoId === v.id ? ' terceiro-card-selecionado' : ''}`}
                          disabled={indisponivel}
                          title={
                            jaCadastrado
                              ? 'Este veículo já tem atendimento neste evento'
                              : associadoIndisponivel
                                ? 'Este evento já tem o veículo do associado'
                                : 'Preencher o cadastro com os dados deste veículo'
                          }
                          onClick={() => handlePreencherComVeiculo(v)}
                        >
                          <div className="terceiro-card-linha">
                            <span className="terceiro-card-placa">{v.plate}</span>
                            <span>{v.model ?? 'Modelo não informado'}</span>
                            <span className="terceiro-card-classe">{CarClassMap[v.tipo] ?? v.tipo}</span>
                            {jaCadastrado && <span className="terceiro-card-badge">já cadastrado</span>}
                            {!jaCadastrado && associadoIndisponivel && (
                              <span className="terceiro-card-badge">associado já cadastrado</span>
                            )}
                            {veiculoSelecionadoId === v.id && (
                              <span className="terceiro-card-badge terceiro-card-badge-ok">selecionado</span>
                            )}
                          </div>
                          <div className="terceiro-card-linha terceiro-card-meta">
                            {v.ownerName && <span>{v.ownerName}</span>}
                            {v.situacaoEvento && <span>Situação no SGA: {v.situacaoEvento}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Row 2: Tipo de veículo, Ano fabricação, Ano modelo */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Tipo de veículo</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" value={vehicleType} onChange={(e) => setVehicleType(e.target.value as 'car' | 'motorcycle')}>
                        <option value="car">Carro</option>
                        <option value="motorcycle">Moto</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Ano de fabricação</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select
                        className="Input"
                        value={anoFabricacao}
                        onChange={(e) => handleAnoFabricacaoChange(e.target.value === '' ? '' : Number(e.target.value))}
                      >
                        <option value="">Selecione</option>
                        {YEAR_RANGE.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Ano do modelo</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select
                        className="Input"
                        value={modelYear}
                        onChange={(e) => handleModelYearChange(e.target.value === '' ? '' : Number(e.target.value))}
                        disabled={anoFabricacao === ''}
                      >
                        <option value="">{anoFabricacao === '' ? 'Selecione o ano de fabricação primeiro' : 'Selecione'}</option>
                        {compatibleModelYears.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Marca, Modelo (buttons) */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Marca</div>
                  <div className="CampoInput">
                    <button
                      type="button"
                      className="Input fipe-picker-btn"
                      disabled={!yearsReady}
                      onClick={() => setShowBrandPicker(true)}
                    >
                      {selectedBrand ? selectedBrand.name : yearsReady ? 'Selecionar marca...' : 'Selecione os anos primeiro'}
                    </button>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Modelo</div>
                  <div className="CampoInput">
                    <button
                      type="button"
                      className="Input fipe-picker-btn"
                      disabled={!brandReady}
                      onClick={() => setShowModelPicker(true)}
                    >
                      {selectedModel ? selectedModel.name : brandReady ? 'Selecionar modelo...' : 'Selecione a marca primeiro'}
                    </button>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Cor</div>
                  <div className="DropDownFormulRio">
                    <input className="Input" type="text" value={color} onChange={(e) => setColor(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Row 4: Classe, Tipo dano */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Classe do Veículo <span className="required-asterisk">*</span></div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" value={carClass} onChange={(e) => setCarClass(e.target.value as CarClass)} required>
                        <option value="">Selecione a classe do veículo</option>
                        {CarClassOptions.map((v) => (
                          <option key={v} value={v} disabled={v === 'ASSOCIADO' && associadoBloqueado}>
                            {CarClassMap[v]}{v === 'ASSOCIADO' && associadoBloqueado ? ' (indisponível neste caminho)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Tipo de dano</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" value={damageType} onChange={(e) => setDamageType(e.target.value as DamageType)}>
                        <option value="">Selecione o tipo de dano</option>
                        {DamageTypeOptions.map((v) => <option key={v} value={v}>{DamageTypeMap[v]}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* at-89: as tags de esteira saíram do cadastro e da edição do veículo (decisão
                  do Victor, 03/09/2026). Elas passaram a ter ação própria — "Atualizar Motivo da
                  Pendência", no modal do veículo —, que mostra só as tags da esteira ATUAL. Aqui
                  o formulário exibia as de TODAS as esteiras de uma vez, o que com uma tag de
                  mesmo nome por esteira significava seis campos idênticos e indistinguíveis. */}

              {/* Row 5: Data entrada, Dias oficina externa */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Data de entrada</div>
                  <div className="InputData">
                    <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="Input" />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Dias em Oficina Externa</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      value={externalShopDays}
                      onChange={(e) => { if (/^\d*$/.test(e.target.value)) setExternalShopDays(e.target.value); }}
                    />
                  </div>
                </div>
              </div>

              {/* Row 6: RENAVAM, N° BO */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Renavam</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" value={renavam} onChange={(e) => setRenavam(e.target.value)} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">N° do Boletim de Ocorrência</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" value={numeroBo} onChange={(e) => setNumeroBo(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Row 7: Dados do proprietário (associado ou terceiro) */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo" title="Nome do proprietário (associado ou terceiro)">Nome do proprietário</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Telefone do proprietário</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={ownerPhone}
                      onChange={(e) => setOwnerPhone(formatPhoneBr(e.target.value))}
                    />
                  </div>
                </div>
                {/* at-76: é por este número que a rastreadora localiza o equipamento —
                    placa e chassi não localizam (medido na API deles em 19/08/2026). */}
                <div className="Campo">
                  <div className="CampoTitulo">IMEI do rastreador</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      inputMode="numeric"
                      placeholder="359510086137028"
                      value={rastreadorImei}
                      onChange={(e) => setRastreadorImei(e.target.value.replace(/\D/g, '').slice(0, 17))}
                    />
                  </div>
                </div>
              </div>

              {/* Row 8: Observações do atendimento (at-54) */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Observações do Cliente</div>
                  <div className="CampoInput observacoes-campo">
                    <textarea
                      className="Input observacoes-textarea"
                      rows={3}
                      maxLength={OBSERVACOES_MAX_LENGTH}
                      value={observacoesCliente}
                      onChange={(e) => setObservacoesCliente(e.target.value)}
                    />
                  </div>
                  <span className="observacoes-hint">
                    O que o cliente relatou ou pediu. {OBSERVACOES_MAX_LENGTH - observacoesCliente.length} caracteres restantes.
                  </span>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Observações da Oficina</div>
                  <div className="CampoInput observacoes-campo">
                    <textarea
                      className="Input observacoes-textarea"
                      rows={3}
                      maxLength={OBSERVACOES_MAX_LENGTH}
                      value={observacoesOficina}
                      onChange={(e) => setObservacoesOficina(e.target.value)}
                    />
                  </div>
                  <span className="observacoes-hint">
                    Anotações internas da equipe. {OBSERVACOES_MAX_LENGTH - observacoesOficina.length} caracteres restantes.
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="Frame1000003998">
                <div className="CtaBigEditar cancel" onClick={requestClose}>
                  <button className="CustomFileCancel" type="button">Cancelar</button>
                </div>
                <button className="CtaBigEditar save" type="submit" disabled={isLoading || isPlateChecking || isPlateBlocked}>
                  {isLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* FIPE pickers — rendered outside form to avoid z-index issues */}
      <FipePickerModal
        isOpen={showBrandPicker}
        title="Selecionar Marca"
        searchPlaceholder="Buscar marca..."
        fetchFn={fetchBrands}
        onSelect={handleBrandSelect}
        onCancel={() => setShowBrandPicker(false)}
      />
      <FipePickerModal
        isOpen={showModelPicker}
        title="Selecionar Modelo"
        searchPlaceholder="Buscar modelo..."
        fetchFn={fetchModels}
        onSelect={handleModelSelect}
        onCancel={() => setShowModelPicker(false)}
      />
      <ConfirmModal
        isOpen={discardConfirmOpen}
        message="Existem dados preenchidos que serão perdidos. Deseja descartar o cadastro?"
        confirmLabel="Descartar"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          // Descartar o cadastro descarta também o rascunho — senão ele voltaria na próxima abertura.
          void rascunho.descartar();
          onCancel();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Cadastro de Veículo"
        secoes={helpSections}
      />
    </div>
  );
};

export default AddCarModal;
